import { DayValue } from "@/constants/days";
/**
 * Date and time utility functions for overtime calculations
 */

/**
 * Parse time string to seconds (24-hour format)
 * Supports both HH:MM and HH:MM:SS formats
 * @param timeString - Time in "HH:MM" or "HH:MM:SS" format
 * @returns Number of seconds since midnight, or null if invalid
 */
export function parseTimeToSeconds(timeString: string): number | null {
  if (!timeString || typeof timeString !== 'string') return null;
  
  // Accept HH:MM or HH:MM:SS, 24-hour format
  const timeMatch = timeString.trim().match(/^(\d{1,2}):([0-5]\d)(?::([0-5]\d))?$/);
  if (!timeMatch) return null;
  
  const hours = Number(timeMatch[1]);
  const minutes = Number(timeMatch[2]);
  const seconds = timeMatch[3] ? Number(timeMatch[3]) : 0;
  
  // Validate hour range (0-23)
  if (hours < 0 || hours > 23) return null;
  
  return hours * 3600 + minutes * 60 + seconds;
}

/**
 * Calculate duration in hours between two times, handling cross-midnight shifts
 * @param startTime - Start time in "HH:MM" or "HH:MM:SS" format
 * @param endTime - End time in "HH:MM" or "HH:MM:SS" format
 * @returns Duration in hours rounded to 2 decimals, or null if invalid
 */
export function calculateTimeDurationHours(startTime: string, endTime: string): number | null {
  const startSeconds = parseTimeToSeconds(startTime);
  const endSeconds = parseTimeToSeconds(endTime);
  
  if (startSeconds === null || endSeconds === null) return null;
  
  // Calculate duration in seconds
  let durationSeconds = endSeconds - startSeconds;
  
  // Handle cross-midnight shifts (night shifts)
  // If end time is earlier than start time, assume it's next day
  if (durationSeconds < 0) {
    durationSeconds += 24 * 3600; // Add 24 hours in seconds
  }
  
  // Validate duration constraints
  if (durationSeconds <= 0) return null; // No zero or negative durations
  if (durationSeconds > 24 * 3600) return null; // No durations > 24 hours
  
  // Convert to hours and round to 2 decimal places
  const hours = durationSeconds / 3600;
  return Number(hours.toFixed(2));
}

/**
 * Validate time input format and constraints
 * @param timeString - Time string to validate
 * @returns Object with validation result and error message
 */
export function validateTimeInput(timeString: string): { isValid: boolean; error?: string } {
  if (!timeString || timeString.trim() === '') {
    return { isValid: false, error: 'Time is required' };
  }
  
  const timeMatch = timeString.trim().match(/^(\d{1,2}):([0-5]\d)(?::([0-5]\d))?$/);
  if (!timeMatch) {
    return { isValid: false, error: 'Invalid time format. Use HH:MM or HH:MM:SS (24-hour)' };
  }
  
  const hours = Number(timeMatch[1]);
  if (hours < 0 || hours > 23) {
    return { isValid: false, error: 'Hours must be between 00 and 23' };
  }
  
  return { isValid: true };
}

/**
 * Validate duration between two times
 * @param startTime - Start time string
 * @param endTime - End time string
 * @returns Object with validation result and error message
 */
export function validateTimeDuration(startTime: string, endTime: string): { isValid: boolean; error?: string; hours?: number } {
  // First validate individual time inputs
  const startValidation = validateTimeInput(startTime);
  if (!startValidation.isValid) {
    return { isValid: false, error: `Start time: ${startValidation.error}` };
  }
  
  const endValidation = validateTimeInput(endTime);
  if (!endValidation.isValid) {
    return { isValid: false, error: `End time: ${endValidation.error}` };
  }
  
  // Calculate duration
  const hours = calculateTimeDurationHours(startTime, endTime);
  if (hours === null) {
    return { isValid: false, error: 'Invalid time range. Duration must be greater than 0 and not exceed 24 hours' };
  }
  
  return { isValid: true, hours };
}

/**
 * Check if overtime period overlaps with shift working hours
 * Handles cross-midnight shifts for both OT and shift times
 * @param otStartTime - OT start time in "HH:MM" format
 * @param otEndTime - OT end time in "HH:MM" format  
 * @param shiftStartTime - Shift start time in "HH:MM" format
 * @param shiftEndTime - Shift end time in "HH:MM" format
 * @returns boolean indicating if there's an overlap
 */
export function isOvertimeOverlapWithShift(
  otStartTime: string,
  otEndTime: string,
  shiftStartTime: string,
  shiftEndTime: string
): boolean {
  // Parse all times to seconds
  const otStart = parseTimeToSeconds(otStartTime);
  const otEnd = parseTimeToSeconds(otEndTime);
  const shiftStart = parseTimeToSeconds(shiftStartTime);
  const shiftEnd = parseTimeToSeconds(shiftEndTime);

  // If any time parsing failed, no overlap detected (invalid input)
  if (otStart === null || otEnd === null || shiftStart === null || shiftEnd === null) {
    return false;
  }

  // Handle cross-midnight scenarios for overlap detection
  // For cross-midnight ranges, we need to check both the current day and next day portions
  
  const isOtCrossMidnight = otEnd < otStart;
  const isShiftCrossMidnight = shiftEnd < shiftStart;
  
  let hasOverlap = false;
  
  if (!isOtCrossMidnight && !isShiftCrossMidnight) {
    // Both ranges are same-day, simple overlap check
    hasOverlap = otStart < shiftEnd && shiftStart < otEnd;
  } else if (isOtCrossMidnight && !isShiftCrossMidnight) {
    // OT crosses midnight, shift is same-day
    // Check if shift overlaps with either the evening part (otStart to 24:00) or morning part (00:00 to otEnd)
    hasOverlap = (otStart < shiftEnd && shiftStart < 24 * 3600) || (0 < shiftEnd && shiftStart < otEnd);
  } else if (!isOtCrossMidnight && isShiftCrossMidnight) {
    // Shift crosses midnight, OT is same-day
    // Check if OT overlaps with either the evening part (shiftStart to 24:00) or morning part (00:00 to shiftEnd)
    hasOverlap = (shiftStart < otEnd && otStart < 24 * 3600) || (0 < otEnd && otStart < shiftEnd);
  } else {
    // Both cross midnight - they will overlap unless completely separated
    // OT: otStart->24:00 + 00:00->otEnd, Shift: shiftStart->24:00 + 00:00->shiftEnd
    // Check all possible overlaps
    
    // Both cross midnight - use simplified 48-hour timeline approach
    // Convert both to 48-hour timeline and check overlap
    const otStartNorm = otStart;
    const otEndNorm = otEnd + 24 * 3600; // Next day
    const shiftStartNorm = shiftStart;  
    const shiftEndNorm = shiftEnd + 24 * 3600; // Next day
    
    hasOverlap = otStartNorm < shiftEndNorm && shiftStartNorm < otEndNorm;
  }

  return hasOverlap;
}

/**
 * Check if overtime period is COMPLETELY within shift working hours
 * This is stricter than overlap - both start and end must be within shift bounds
 * @param otStartTime - OT start time in "HH:MM" format
 * @param otEndTime - OT end time in "HH:MM" format  
 * @param shiftStartTime - Shift start time in "HH:MM" format
 * @param shiftEndTime - Shift end time in "HH:MM" format
 * @returns boolean indicating if OT is completely within shift hours
 */
export function isOvertimeCompletelyWithinShift(
  otStartTime: string,
  otEndTime: string,
  shiftStartTime: string,
  shiftEndTime: string
): boolean {
  // Parse all times to seconds
  const otStart = parseTimeToSeconds(otStartTime);
  const otEnd = parseTimeToSeconds(otEndTime);
  const shiftStart = parseTimeToSeconds(shiftStartTime);
  const shiftEnd = parseTimeToSeconds(shiftEndTime);

  // If any time parsing failed, return false (invalid input)
  if (otStart === null || otEnd === null || shiftStart === null || shiftEnd === null) {
    return false;
  }

  // Handle cross-midnight scenarios
  const isOtCrossMidnight = otEnd < otStart;
  const isShiftCrossMidnight = shiftEnd < shiftStart;
  
  if (!isOtCrossMidnight && !isShiftCrossMidnight) {
    // Both ranges are same-day
    // OT must be completely within shift: otStart >= shiftStart && otEnd <= shiftEnd
    return otStart >= shiftStart && otEnd <= shiftEnd;
  } else if (isOtCrossMidnight && isShiftCrossMidnight) {
    // Both cross midnight - more complex
    // For simplicity, use 48-hour timeline
    const otStartNorm = otStart;
    const otEndNorm = otEnd + 24 * 3600; // Next day
    const shiftStartNorm = shiftStart;  
    const shiftEndNorm = shiftEnd + 24 * 3600; // Next day
    
    return otStartNorm >= shiftStartNorm && otEndNorm <= shiftEndNorm;
  } else {
    // One crosses midnight, other doesn't - they can't be completely within each other
    return false;
  }
}

/**
 * Get overlap validation result with detailed error message
 * @param otStartTime - OT start time
 * @param otEndTime - OT end time
 * @param shiftStartTime - Shift start time
 * @param shiftEndTime - Shift end time
 * @param inWorkingHours - Whether the shift is a working hours shift (true) or overtime shift (false)
 * @returns Object with validation result and error message
 */
export function validateOvertimeShiftOverlap(
  otStartTime: string,
  otEndTime: string,
  shiftStartTime: string,
  shiftEndTime: string,
  inWorkingHours: boolean = false
): { hasOverlap: boolean; error?: string } {
  // First validate that we have valid times
  if (!otStartTime || !otEndTime || !shiftStartTime || !shiftEndTime) {
    return { hasOverlap: false };
  }

  const hasOverlap = isOvertimeOverlapWithShift(otStartTime, otEndTime, shiftStartTime, shiftEndTime);

  // Apply validation based on in_working_hours field
  if (inWorkingHours) {
    // If in_working_hours = TRUE: OT must be WITHIN shift hours
    if (!hasOverlap) {
      return {
        hasOverlap: true, // Return true to indicate validation failure
        error: `For this shift, overtime must be within shift hours (${shiftStartTime} - ${shiftEndTime})`
      };
    }
  } else {
    // If in_working_hours = FALSE: OT must NOT overlap with shift hours (existing logic)
    if (hasOverlap) {
      return {
        hasOverlap: true,
        error: `Overtime cannot overlap with shift hours (${shiftStartTime} - ${shiftEndTime})`
      };
    }
  }

  return { hasOverlap: false };
}

// Simple cache for public holiday checks with expiration
const holidayCache = new Map<string, { result: boolean; timestamp: number }>();
const CACHE_DURATION = 24 * 60 * 60 * 1000; // 24 hours in milliseconds

/**
 * Check if a date is a public holiday
 * @param date - Date string in YYYY-MM-DD format
 * @param companyId - Company ID for holiday checking
 * @returns Promise<boolean>
 */
export async function isPublicHoliday(date: string, companyId: string): Promise<boolean> {
  const cacheKey = `${date}-${companyId}`;
  const now = Date.now();
  
  // Check cache first
  const cached = holidayCache.get(cacheKey);
  if (cached && (now - cached.timestamp) < CACHE_DURATION) {
    return cached.result;
  }
  
  try {
    // This would typically check against a holidays table
    // For now, return false as a placeholder
    const result = false;
    
    // Cache the result with timestamp
    holidayCache.set(cacheKey, { result, timestamp: now });
    
    // Clean up expired entries occasionally (e.g., every 10th cache miss) to improve performance
    if (holidayCache.size > 100 && Math.random() < 0.1) {
      const expiredKeys = Array.from(holidayCache.entries())
        .filter(([, data]) => (now - data.timestamp) >= CACHE_DURATION)
        .map(([key]) => key);
      expiredKeys.forEach(key => holidayCache.delete(key));
    }
    
    return result;
  } catch (error) {
    console.error('Error checking public holiday:', error);
    return false;
  }
}

/**
 * Check if a date is an effective public holiday (real holiday or weekend based on shift)
 * @param date - Date string in YYYY-MM-DD format
 * @param shift - Employee shift type
 * @param companyId - Company ID for holiday checking
 * @returns Promise<boolean>
 */
export async function isEffectivePublicHoliday(date: string, shift: string, companyId: string): Promise<boolean> {
  try {
    // Check if it's a real public holiday
    const isHoliday = await isPublicHoliday(date, companyId);
    if (isHoliday) {
      return true;
    }

    // For weekend workers, weekends are not effective holidays
    if (shift === 'night' || shift === 'day3') {
      return false;
    }

    // For regular workers, weekends are effective holidays
    const dayOfWeek = new Date(date).getDay();
    return dayOfWeek === DayValue.Sun || dayOfWeek === DayValue.Sat; // Sunday or Saturday
  } catch (error) {
    console.error('Error checking effective public holiday:', error);
    return false;
  }
}

/**
 * Get working hours for a shift
 * @param shift - Employee shift type
 * @param date - Date string in YYYY-MM-DD format
 * @returns Object with start and end times
 */
export function getWorkingHoursForShift(): { start: string; end: string } {
  // Default working hours (8 AM to 5 PM)
  const defaultHours = { start: '08:00', end: '17:00' };

  // For now, return default hours
  // This could be enhanced to fetch from database based on shift configuration
  return defaultHours;
}

/**
 * Check if overtime request is outside working hours
 * @param startTime - Overtime start time in HH:MM format
 * @param endTime - Overtime end time in HH:MM format
 * @param workingHours - Working hours object with start and end times
 * @returns boolean
 */
export function isOutsideWorkingHours(startTime: string, endTime: string, workingHours: { start: string; end: string }): boolean {
  try {
    // Convert times to minutes for comparison
    const convertTimeToMinutes = (time: string): number => {
      const [hours, minutes] = time.split(':').map(Number);
      return hours * 60 + minutes;
    };

    const overtimeStart = convertTimeToMinutes(startTime);
    const overtimeEnd = convertTimeToMinutes(endTime);
    const workStart = convertTimeToMinutes(workingHours.start);
    const workEnd = convertTimeToMinutes(workingHours.end);

    // Handle overnight shifts
    if (overtimeEnd < overtimeStart) {
      // Overtime spans midnight
      return overtimeStart >= workEnd || overtimeEnd <= workStart;
    }

    // Check if overtime is completely outside working hours
    return overtimeEnd <= workStart || overtimeStart >= workEnd;
  } catch (error) {
    console.error('Error checking if overtime is outside working hours:', error);
    return false; // Default to not allowed if error (conservative approach)
  }
} 